import { createTheme, SimplePaletteColorOptions, Theme, ThemeOptions } from '@mui/material'
import { merge, omit } from 'lodash-es'

/**
 * Compose a theme from a structural base and a colored overlay.
 *
 * Palette composition is atomic per key: each key the overlay provides fully
 * replaces the corresponding key on the base. This prevents base `contrastText`
 * from leaking onto an overlay-overridden `main`. `common` is shallow-merged so
 * the overlay can override individual entries (e.g. `highlight`) without
 * dropping base defaults.
 *
 * `selected` is a brand-owned palette key not augmented by MUI's `createTheme`.
 * `composeTheme` runs `palette.augmentColor` on the composed `selected` (or
 * derives it from `primary.main` if the overlay omitted it) so consumers always
 * see fully populated `light`/`dark`/`contrastText`.
 *
 * Non-palette overlay fields (`border`, `typography`, `components`, etc.) are
 * deep-merged onto the base.
 *
 * @throws if the composed palette has no `primary` — overlays must provide one.
 */
export const composeTheme = (base: Theme, overlay: ThemeOptions | null): Theme => {
  const overlayPalette = overlay?.palette ?? {}

  const mergedPalette: Record<string, unknown> = { ...base.palette }
  for (const [key, value] of Object.entries(overlayPalette)) {
    if (key === 'common') {
      mergedPalette['common'] = { ...base.palette.common, ...(value as object) }
    } else {
      mergedPalette[key] = value
    }
  }

  if (overlay) {
    const overlayPrimary = overlay.palette?.primary as SimplePaletteColorOptions | undefined
    if (!overlayPrimary || !('main' in overlayPrimary) || !overlayPrimary.main) {
      throw new Error('composeTheme: overlay must provide palette.primary with a main color')
    }
  }

  const primary = mergedPalette['primary'] as SimplePaletteColorOptions | undefined
  const selectedInput =
    (mergedPalette['selected'] as SimplePaletteColorOptions | undefined) ??
    (primary?.main ? { main: primary.main as string } : undefined)
  if (selectedInput) {
    mergedPalette['selected'] = base.palette.augmentColor({ color: selectedInput })
  }

  const themeOptions = merge({}, omit(base, 'palette'), omit(overlay ?? {}, 'palette'), {
    palette: mergedPalette,
  })

  return createTheme(themeOptions)
}
